import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Globe, Clock, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { StandaloneTopBar } from "@/components/Layout/StandaloneTopBar";
import { supabase } from "@/integrations/supabase/client";

export default function Language() {
  const { toast } = useToast();
  const { i18n, t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [timezone, setTimezone] = useState("Africa/Nairobi");
  const [dateFormat, setDateFormat] = useState("DD/MM/YYYY");
  const [timeFormat, setTimeFormat] = useState("24h");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadPreferences = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: preferences } = await supabase
          .from('user_preferences')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (preferences) {
          setSelectedLanguage(preferences.language || 'en');
          setTimezone(preferences.timezone || 'UTC');
          setDateFormat(preferences.date_format || 'DD/MM/YYYY');
          setTimeFormat(preferences.time_format || '24h');
          i18n.changeLanguage(preferences.language || 'en');
        }
      }
    };

    loadPreferences();
  }, [i18n]);

  const languages = [
    { code: "en", name: "English", nativeName: "English" },
    { code: "sw", name: "Swahili", nativeName: "Kiswahili" },
    { code: "fr", name: "French", nativeName: "Français" },
    { code: "es", name: "Spanish", nativeName: "Español" },
    { code: "ar", name: "Arabic", nativeName: "العربية" },
    { code: "pt", name: "Portuguese", nativeName: "Português" }
  ];

  const timezones = [
    "Africa/Nairobi",
    "Africa/Lagos",
    "Africa/Cairo",
    "Africa/Johannesburg",
    "Europe/London",
    "Europe/Paris",
    "America/New_York",
    "Asia/Dubai"
  ];

  const handleSave = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          language: selectedLanguage,
          timezone,
          date_format: dateFormat,
          time_format: timeFormat,
        });

      if (error) throw error;

      // Update i18next language
      await i18n.changeLanguage(selectedLanguage);

      toast({
        title: t('language.settings_saved'),
        description: t('language.settings_saved_description')
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save preferences",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background">
      <StandaloneTopBar backTo="/dashboard" backLabel="Back to Dashboard" />
      <div className="container max-w-4xl py-8 px-6 mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{t('language.title')}</h1>
            <p className="text-justify text-muted-foreground">{t('language.subtitle')}</p>
          </div>

          <div className="space-y-6">
            {/* Language Selection */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  <CardTitle>Language</CardTitle>
                </div>
                <CardDescription>Choose your preferred language</CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup value={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <div className="grid md:grid-cols-2 gap-3">
                    {languages.map((lang) => (
                      <div key={lang.code} className="flex items-center space-x-2 border rounded-lg p-3">
                        <RadioGroupItem value={lang.code} id={lang.code} />
                        <Label htmlFor={lang.code} className="flex-1 cursor-pointer">
                          <div>
                            <p className="text-justify font-medium">{lang.name}</p>
                            <p className="text-justify text-sm text-muted-foreground">{lang.nativeName}</p>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Timezone */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  <CardTitle>Timezone</CardTitle>
                </div>
                <CardDescription>Select your local timezone</CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timezones.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-justify text-sm text-muted-foreground mt-2">
                  Current time: {new Date().toLocaleTimeString('en-US', { timeZone: timezone })}
                </p>
              </CardContent>
            </Card>

            {/* Date & Time Format */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  <CardTitle>Date & Time Format</CardTitle>
                </div>
                <CardDescription>Choose how dates and times are displayed</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="mb-3 block">Date Format</Label>
                  <RadioGroup value={dateFormat} onValueChange={setDateFormat}>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="DD/MM/YYYY" id="date1" />
                        <Label htmlFor="date1" className="cursor-pointer">DD/MM/YYYY (31/12/2024)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="MM/DD/YYYY" id="date2" />
                        <Label htmlFor="date2" className="cursor-pointer">MM/DD/YYYY (12/31/2024)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="YYYY-MM-DD" id="date3" />
                        <Label htmlFor="date3" className="cursor-pointer">YYYY-MM-DD (2024-12-31)</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                <Separator />

                <div>
                  <Label className="mb-3 block">Time Format</Label>
                  <RadioGroup value={timeFormat} onValueChange={setTimeFormat}>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="24h" id="time1" />
                        <Label htmlFor="time1" className="cursor-pointer">24-hour (13:00)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="12h" id="time2" />
                        <Label htmlFor="time2" className="cursor-pointer">12-hour (1:00 PM)</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>

          <Button onClick={handleSave} size="lg" disabled={loading}>
            {loading ? t('actions.loading') : t('actions.save_changes')}
          </Button>
        </div>
      </div>
    </div>
  );
}
